import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Alert,
  Button,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { motion, useReducedMotion } from 'framer-motion';
import toast from 'react-hot-toast';
import { useSecureAuth } from '../../contexts/SecureAuthContext';
import { apiService, API_ENDPOINTS } from '../../config/api';
import { useSupportForm } from '../../hooks/useSupportForm';
import CategorySelector from './CategorySelector';
import SupportForm from './SupportForm';

interface SupportPanelProps {
  onClose: () => void;
  fabRef: React.RefObject<HTMLButtonElement | null>;
}

const MotionPaper = motion.create(Paper);

const SupportPanel: React.FC<SupportPanelProps> = ({ onClose, fabRef }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const shouldReduceMotion = useReducedMotion();
  const panelRef = useRef<HTMLDivElement>(null);
  const { user } = useSecureAuth();

  const {
    formData,
    setField,
    resetCategory,
    isSubmitting,
    submitStatus,
    error,
    submitForm,
    hasDraft,
    restoreDraft,
    dismissDraft,
    resetForm,
    detectedStudyName,
    addScreenshots,
    removeScreenshot,
  } = useSupportForm();

  const [studies, setStudies] = useState<Array<{ id: string; name: string }>>([]);

  // Fetch researcher's studies for the study selector
  useEffect(() => {
    apiService
      .get(API_ENDPOINTS.AUTH.RESEARCHER_STUDIES)
      .then((res: any) => {
        const list = res?.studies || res?.data || res || [];
        if (Array.isArray(list)) {
          setStudies(list.map((s: any) => ({ id: s.id, name: s.name })));
        }
      })
      .catch(() => {
        // non-critical
      });
  }, []);

  // Focus management — focus panel on open
  useEffect(() => {
    const timer = setTimeout(() => {
      panelRef.current?.focus();
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  // Escape key handler
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  // Auto-close on success + toast
  useEffect(() => {
    if (submitStatus === 'success') {
      toast.success("Support request sent — we'll get back to you within 24 hours", {
        duration: 5000,
      });
      const timer = setTimeout(() => {
        resetForm();
        onClose();
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [submitStatus, onClose, resetForm]);

  // Return focus to FAB on close
  const handleClose = () => {
    onClose();
    // Allow exit animation, then focus FAB
    setTimeout(() => {
      fabRef.current?.focus();
    }, 250);
  };

  const researcherName = user?.displayName || 'Researcher';
  const researcherEmail = user?.email || '';

  return (
    <MotionPaper
      ref={panelRef}
      tabIndex={-1}
      role="dialog"
      aria-modal="false"
      aria-labelledby="support-panel-title"
      elevation={8}
      initial={
        shouldReduceMotion
          ? { opacity: 0 }
          : { opacity: 0, y: 40, scale: 0.95 }
      }
      animate={
        shouldReduceMotion
          ? { opacity: 1 }
          : { opacity: 1, y: 0, scale: 1 }
      }
      exit={
        shouldReduceMotion
          ? { opacity: 0 }
          : { opacity: 0, y: 20 }
      }
      transition={
        shouldReduceMotion
          ? { duration: 0.15 }
          : submitStatus === 'success'
            ? { duration: 0.2, ease: 'easeOut' }
            : { type: 'spring', damping: 25, stiffness: 300 }
      }
      sx={{
        position: 'fixed',
        zIndex: 1400,
        outline: 'none',
        borderRadius: isMobile ? '16px 16px 0 0' : '16px',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        ...(isMobile
          ? {
              bottom: 0,
              left: 0,
              right: 0,
              width: '100%',
              maxHeight: '85vh',
            }
          : {
              bottom: 96,
              right: 24,
              width: 400,
              maxWidth: 'calc(100vw - 48px)',
              maxHeight: '70vh',
            }),
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2,
          py: 1.5,
          borderBottom: `1px solid ${theme.palette.divider}`,
          flexShrink: 0,
        }}
      >
        <Typography
          id="support-panel-title"
          variant="subtitle1"
          sx={{ fontWeight: 600 }}
        >
          How can we help?
        </Typography>
        <IconButton
          size="small"
          onClick={handleClose}
          aria-label="Close support panel"
        >
          <Close fontSize="small" />
        </IconButton>
      </Box>

      {/* Body — scrollable */}
      <Box
        sx={{
          overflowY: 'auto',
          px: 2,
          py: 2,
          flexGrow: 1,
        }}
      >
        {/* Draft restore banner */}
        {hasDraft && !formData.category && (
          <Alert
            severity="info"
            sx={{ mb: 2, py: 0.5 }}
            action={
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                <Button color="inherit" size="small" onClick={restoreDraft}>
                  Restore
                </Button>
                <Button color="inherit" size="small" onClick={dismissDraft}>
                  Discard
                </Button>
              </Box>
            }
          >
            <Typography variant="caption">You have a saved draft</Typography>
          </Alert>
        )}

        {/* Layer 1: Category selector (no category selected yet) */}
        {/* Layer 2: Form with category-specific fields */}
        {!formData.category ? (
          <CategorySelector
            selected={formData.category}
            onSelect={(cat) => setField('category', cat)}
          />
        ) : (
          <SupportForm
            formData={formData}
            setField={setField}
            onBack={resetCategory}
            onSubmit={submitForm}
            isSubmitting={isSubmitting}
            submitStatus={submitStatus}
            error={error}
            researcherName={researcherName}
            researcherEmail={researcherEmail}
            detectedStudyName={detectedStudyName}
            addScreenshots={addScreenshots}
            removeScreenshot={removeScreenshot}
            studies={studies}
          />
        )}
      </Box>
    </MotionPaper>
  );
};

export default SupportPanel;
